import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { relationshipAPI, platformAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { extractErrorMessage } from '@/lib/errors';
import { Users, UserPlus, Phone, CheckCircle, XCircle, Clock, Loader2, Search, Building, MapPin, Check, Package } from 'lucide-react';

// Which user types a Network page invites from the directory and lists
// under connections / pending invites. Follows the connection rule in
// backend/connection_rules.py.
const PAGE_TYPES = {
  distributors: ['distributor'],
  suppliers: ['manufacturer'],
  shopkeepers: ['shopkeeper'],
  customers: ['farmer'],
};

function inviteTypesFor(page, role) {
  // Farmers can't connect with manufacturers; their suppliers are
  // shopkeepers and distributors.
  if (page === 'suppliers' && role === 'farmer') return ['shopkeeper', 'distributor'];
  return PAGE_TYPES[page] || [];
}

export default function NetworkPage({ type }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [network, setNetwork] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [phone, setPhone] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteTab, setInviteTab] = useState('list');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  // IDs of directory users the current session has already invited — kept
  // separately so we can show an "Invited" pill on their row (instead of
  // removing them entirely, which used to make invited people disappear
  // without a trace).
  const [invitedIds, setInvitedIds] = useState(new Set());
  const [searchParams, setSearchParams] = useSearchParams();

  // `?invite=<name>` (from the home search) opens the invite dialog
  // filtered to that business, then drops the param so a refresh or
  // the back button doesn't reopen it.
  useEffect(() => {
    const name = searchParams.get('invite');
    if (!name) return;
    setSearchQuery(name);
    setInviteTab('list');
    setShowInvite(true);
    const next = new URLSearchParams(searchParams);
    next.delete('invite');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // A distributor adds shops on the Add a shop page (PR 66), where they
  // also tick which products each shop gets.
  const navigate = useNavigate();
  const shopsForDistributor = user?.user_type === 'distributor' && type === 'shopkeepers';
  useEffect(() => {
    if (shopsForDistributor && showInvite) navigate('/shopkeepers/add');
  }, [shopsForDistributor, showInvite, navigate]);

  const inviteTypes = inviteTypesFor(type, user?.user_type);
  const listTypes = inviteTypes;
  const showRoleOnRows = inviteTypes.length > 1;

  const titles = type === 'suppliers' && user?.user_type === 'farmer'
    ? t('network.titles.farmerSuppliers', 'Suppliers')
    : t(`network.titles.${type}`, 'Network');
  const singularRole = titles.endsWith('s') ? titles.slice(0, -1) : titles;
  // The current user's UPSTREAM role, if any — the party they can't invite,
  // only request appointment from. Everything else in the supply-chain
  // graph is still an invite.
  const UPSTREAM_OF = { distributor: 'manufacturer', shopkeeper: 'distributor' };
  const targetRoleForPage = inviteTypes[0];
  const isRequestMode = Boolean(
    targetRoleForPage && targetRoleForPage === UPSTREAM_OF[user?.user_type],
  );
  const actionLabel      = isRequestMode ? t('network.request')       : t('network.invite');
  const actionLabelDone  = isRequestMode ? t('network.requested')     : t('network.invited');
  const actionLabelRole  = isRequestMode
    ? t('network.requestRole', { role: singularRole })
    : t('network.inviteRole', { role: singularRole });
  const actionSubmitLabel = isRequestMode ? t('network.sendRequest') : t('network.sendInvite');
  const toastSentKey      = isRequestMode ? 'network.toast.requestSent'   : 'network.toast.sent';
  const toastSentToKey    = isRequestMode ? 'network.toast.requestSentTo' : 'network.toast.sentTo';
  const emptySentKey      = isRequestMode ? 'network.empty.sentPendingRequests' : 'network.empty.sentPending';

  useEffect(() => {
    loadNetwork();
  }, [user.id, type]);

  useEffect(() => {
    if (showInvite) {
      loadAvailableUsers();
    }
  }, [showInvite, type]);

  const loadNetwork = async () => {
    try {
      const [networkRes, pendingRes] = await Promise.all([
        relationshipAPI.getNetwork(user.id),
        relationshipAPI.getPending(user.id),
      ]);

      const networkData = networkRes.data || {};
      const connections = [];

      (networkData.upstream || []).forEach(rel => {
        connections.push({ ...rel, partner_id: rel.from_user_id, partner_name: rel.sender_name, partner_type: rel.sender_type, direction: 'upstream' });
      });
      (networkData.downstream || []).forEach(rel => {
        connections.push({ ...rel, partner_id: rel.to_user_id, partner_name: rel.recipient_name, partner_type: rel.recipient_type, direction: 'downstream' });
      });

      setNetwork(connections);
      setPending(pendingRes.data || []);
    } catch (error) {
      console.error('Failed to load network', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    setLoadingUsers(true);
    try {
      if (!inviteTypes.length) return;

      const responses = await Promise.all(inviteTypes.map((ut) => platformAPI.getUsersByType(ut)));
      const allUsers = responses.flatMap((res) => res.data?.users || []);
      // Filter out people already CONNECTED — but keep those with a pending
      // invite visible so the button can render as "Invited". The
      // isInvitedRow helper decides which state each row renders in.
      const connectedIds = new Set([
        ...network.map(c => c.partner_id),
        user.id,
      ]);
      setAvailableUsers(allUsers.filter(u => !connectedIds.has(u.id)));
    } catch (error) {
      console.error('Failed to load available users', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const relationshipTypeFor = () => {
    if (user.user_type === 'manufacturer' && type === 'distributors') return 'manufacturer_distributor';
    if (user.user_type === 'distributor' && type === 'shopkeepers') return 'distributor_shopkeeper';
    return type?.slice(0, -1) || 'partner';
  };

  const handleInviteByPhone = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast({ title: t('network.toast.phoneRequired'), variant: 'destructive' });
      return;
    }

    setInviting(true);
    try {
      await relationshipAPI.invite({ sender_id: user.id, recipient_phone: phone, relationship_type: relationshipTypeFor() });
      toast({ title: t(toastSentKey) });
      setShowInvite(false);
      setPhone('');
      loadNetwork();
    } catch (error) {
      toast({ title: extractErrorMessage(error, t('network.toast.sendFailed')), variant: 'destructive' });
    } finally {
      setInviting(false);
    }
  };

  const handleInviteUser = async (targetUser) => {
    setInviting(true);
    setSelectedUser(targetUser.id);
    try {
      await relationshipAPI.invite({
        sender_id: user.id,
        recipient_id: targetUser.id,
        relationship_type: relationshipTypeFor(),
        message: `${user.full_name || 'A user'} would like to connect with you.`,
      });
      toast({ title: t(toastSentToKey, { name: targetUser.full_name }) });
      setInvitedIds(prev => {
        const next = new Set(prev);
        next.add(targetUser.id);
        return next;
      });
      loadNetwork();
    } catch (error) {
      toast({ title: extractErrorMessage(error, t('network.toast.sendFailed')), variant: 'destructive' });
    } finally {
      setInviting(false);
      setSelectedUser(null);
    }
  };

  const handleAction = async (relationshipId, action) => {
    try {
      await relationshipAPI.action(relationshipId, action);
      toast({ title: t('network.toast.actionSucceeded', { action }) });
      loadNetwork();
    } catch (error) {
      console.error('Network action failed', error);
      toast({ title: t('network.toast.actionFailed'), variant: 'destructive' });
    }
  };

  // Each page shows only connections and invites with its own user type
  // (e.g. /shopkeepers lists shopkeepers, not the user's manufacturers).
  // Rows with no recorded type stay visible rather than vanish.
  const isPageType = (otherType) =>
    !listTypes.length || !otherType || otherType === 'unknown' || listTypes.includes(otherType);
  const pageNetwork = network.filter((c) => isPageType(c.partner_type));
  const pagePending = pending.filter((rel) =>
    isPageType(rel.from_user_id === user.id ? rel.recipient_type : rel.sender_type));

  // Invites this user has sent that are still awaiting a response — used
  // both for the empty-state count and to remember (across page reloads)
  // which directory rows should render as "Invited".
  const sentPending = pagePending.filter((rel) => rel.from_user_id === user.id);
  const sentPendingCount = sentPending.length;
  const sentPendingUserIds = new Set(sentPending.map((rel) => rel.to_user_id));

  const isInvitedRow = (candidateId) => invitedIds.has(candidateId) || sentPendingUserIds.has(candidateId);

  const filteredUsers = availableUsers.filter(u =>
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-farm-primary" /></div>;
  }

  const renderBrowseBody = () => {
    if (loadingUsers) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-farm-primary" />
        </div>
      );
    }
    if (filteredUsers.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>{t('network.noneAvailable', { titles: titles.toLowerCase() })}</p>
          <p className="text-sm mt-1">{t('network.noneAvailableHint')}</p>
        </div>
      );
    }
    return filteredUsers.map((u) => (
      <div key={u.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-farm-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-farm-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium truncate">{u.full_name}</p>
            {showRoleOnRows && u.user_type && (
              <p className="text-xs text-farm-primary" data-testid={`directory-role-${u.id}`}>
                {t(`common.role.${u.user_type}`, u.user_type)}
              </p>
            )}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {(u.business_name || u.company_name || u.shop_name) && (
                <span className="flex items-center gap-1 truncate">
                  <Building className="w-3 h-3" />
                  {u.business_name || u.company_name || u.shop_name}
                </span>
              )}
              {u.location && (
                <span className="flex items-center gap-1 truncate">
                  <MapPin className="w-3 h-3" />
                  {u.location}
                </span>
              )}
            </div>
          </div>
        </div>
        {isInvitedRow(u.id) ? (
          <Button
            size="sm"
            variant="outline"
            disabled
            className="ml-2 flex-shrink-0 text-farm-success border-farm-success cursor-default"
            data-testid={`invited-user-${u.id}`}
          >
            <Check className="w-4 h-4 mr-1" />
            {actionLabelDone}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => handleInviteUser(u)}
            disabled={inviting && selectedUser === u.id}
            className="bg-farm-primary hover:bg-farm-primary-dark ml-2 flex-shrink-0"
            data-testid={`invite-user-${u.id}`}
          >
            {inviting && selectedUser === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : actionLabel}
          </Button>
        )}
      </div>
    ));
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">{titles}</h1>
          <p className="text-muted-foreground">{t('network.subtitle')}</p>
        </div>
        <Dialog open={showInvite} onOpenChange={setShowInvite}>
          <DialogTrigger asChild>
            <Button className="bg-farm-primary hover:bg-farm-primary-dark" data-testid="invite-btn">
              <UserPlus className="w-4 h-4 mr-2" /> {actionLabel}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>{actionLabelRole}</DialogTitle>
            </DialogHeader>

            <Tabs value={inviteTab} onValueChange={setInviteTab} className="mt-4 flex-1 flex flex-col overflow-hidden">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="list">{t('network.tabs.browse', { titles })}</TabsTrigger>
                <TabsTrigger value="phone">{t('network.tabs.phone')}</TabsTrigger>
              </TabsList>

              <TabsContent value="list" className="flex-1 overflow-hidden flex flex-col mt-4">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder={t('network.searchPlaceholder')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
                </div>

                <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                  {renderBrowseBody()}
                </div>

                <p className="text-xs text-muted-foreground mt-3 pt-3 border-t">
                  {t('network.showing', { shown: filteredUsers.length, total: availableUsers.length, titles: titles.toLowerCase() })}
                </p>
              </TabsContent>

              <TabsContent value="phone" className="mt-4">
                <form onSubmit={handleInviteByPhone} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('network.phoneLabel')}</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input placeholder={t('network.phonePlaceholder')} value={phone} onChange={(e) => setPhone(e.target.value)} className="pl-10" data-testid="invite-phone-input" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('network.phoneHint', { role: singularRole.toLowerCase() })}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowInvite(false)} className="flex-1">{t('common.cancel')}</Button>
                    <Button type="submit" disabled={inviting} className="flex-1 bg-farm-primary hover:bg-farm-primary-dark" data-testid="send-invite-btn">
                      {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : actionSubmitLabel}
                    </Button>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Invitations — split into Received (actionable) and
          Sent (read-only "Waiting for X to accept"). Pre-PR 13 both
          groups rendered under one "Pending Invitations" header and
          read `rel.sender_name` which the backend never returned,
          so sender invites showed the viewer's own name as if a
          stranger wanted to connect with them. Each row now shows the
          OTHER party's name — the backend sends both `inviter_name`
          and `invitee_name` + a `direction` flag. */}
      {pagePending.length > 0 && (() => {
        const received = pagePending.filter((rel) => rel.direction !== 'sent' && rel.to_user_id === user.id);
        const sent = pagePending.filter((rel) => rel.direction === 'sent' || rel.from_user_id === user.id);
        return (
          <>
            {received.length > 0 && (
              <Card className="mb-4 border-farm-warning" data-testid="pending-received-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-farm-warning" />
                    {t('network.received.title')} ({received.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {received.map((rel) => {
                    const otherName = rel.inviter_name || rel.sender_name || t('network.pending.unknown');
                    return (
                      <div key={rel.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 bg-farm-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <Users className="w-5 h-5 text-farm-warning" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{otherName}</p>
                            <p className="text-sm text-muted-foreground truncate">
                              {t('network.received.wantsToConnect', { name: otherName })}
                            </p>
                            {rel.message && <p className="text-xs text-muted-foreground mt-1 line-clamp-2 break-words">{rel.message}</p>}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button size="sm" variant="outline" onClick={() => handleAction(rel.id, 'reject')} className="text-destructive" data-testid={`reject-${rel.id}`}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                          <Button size="sm" onClick={() => handleAction(rel.id, 'accept')} className="bg-farm-success hover:bg-farm-success/90" data-testid={`accept-${rel.id}`}>
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {sent.length > 0 && (
              <Card className="mb-6 border-muted" data-testid="pending-sent-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    {t('network.sent.title')} ({sent.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {sent.map((rel) => {
                    const otherName = rel.invitee_name || rel.recipient_name || t('network.pending.unknown');
                    return (
                      <div key={rel.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                          <Users className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{otherName}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {t('network.sent.waitingFor', { name: otherName })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </>
        );
      })()}

      {/* Connected Network */}
      {pageNetwork.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">{t('network.empty.title')}</h3>
            <p className="text-muted-foreground mb-4">{t('network.empty.subtitle')}</p>
            {sentPendingCount > 0 && (
              <p className="text-sm font-medium text-farm-warning mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {t(emptySentKey, { count: sentPendingCount })}
              </p>
            )}
            <Button onClick={() => setShowInvite(true)} className="bg-farm-primary hover:bg-farm-primary-dark">
              <UserPlus className="w-4 h-4 mr-2" /> {actionLabelRole}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pageNetwork.map((connection) => (
            <Card key={connection.id} className="hover:shadow-farm-lg transition-shadow" data-testid={`connection-${connection.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-farm-primary/10 rounded-full flex items-center justify-center">
                    <Users className="w-7 h-7 text-farm-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold">{connection.partner_name || t('network.pending.unknown')}</h4>
                    <p className="text-sm text-muted-foreground">{connection.partner_type
                      ? t(`common.role.${connection.partner_type}`, connection.partner_type)
                      : connection.relationship_type}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${
                      connection.direction === 'upstream' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {t(`network.direction.${connection.direction}`)}
                    </span>
                  </div>
                </div>
                {shopsForDistributor && (
                  <Button variant="outline" className="w-full mt-3" data-testid={`shop-products-${connection.partner_id}`}
                          onClick={() => navigate(`/shopkeepers/${connection.partner_id}/products`)}>
                    <Package className="w-4 h-4 mr-2" /> {t('shopProducts.open')}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
